"use client";

import ComponentLoading from "@/components/component-loading/component-loading";
import { useLoginMutation } from "@/services/api/login-api";
import { handleSnackbarOpen } from "@/services/slices/main-slice";
import { Button } from "@/shadcn/components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/shadcn/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/shadcn/components/ui/form";
import { Input } from "@/shadcn/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { z } from "zod";

const schema = z.object({
	username: z.string().nonempty("نام کاربری لازم است"),
	password: z.string().nonempty("پسورد نیاز است"),
});

type LoginFormInputs = z.infer<typeof schema>;

const LoginPage: React.FC = () => {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const router = useRouter();
	const [loginTrigger] = useLoginMutation();
	const form = useForm<LoginFormInputs>({
		resolver: zodResolver(schema),
	});

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = form;

	useEffect(() => {
		if (typeof window !== "undefined") {
			localStorage?.removeItem("token");
		}
	}, []);

	const onSubmit = async (data: LoginFormInputs) => {
		try {
			setLoading(true);
			const response = await loginTrigger(data).unwrap();

			if (typeof window !== "undefined") {
				localStorage?.setItem("token", response.token);
			}

			const params = new URLSearchParams(window.location.search);
			const redirectUrl = params.get("redirect_url");

			if (redirectUrl) {
				try {
					const decodedUrl = atob(redirectUrl);
					window.location.href = decodedUrl;
				} catch (error) {
					console.error("Failed to decode redirect_url:", error);
					router.push("/panel/dashboard");
				}
			} else {
				router.push("/panel/dashboard");
			}
		} catch (error: any) {
			setLoading(false);

			console.error("Error during login:", error);

			// ✅ Check backend response status
			if (error?.status === 403) {
				dispatch(
					handleSnackbarOpen({
						message: "اکانت شما مسدود است",
						severity: "error",
					})
				);
			} else {
				dispatch(
					handleSnackbarOpen({
						message: "پسورد اشتباه است",
						severity: "error",
					})
				);
			}
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
			<Card className="w-full max-w-md p-8 bg-gray-800 rounded-lg shadow-md border-none">
				<CardHeader>
					<CardTitle className="text-2xl font-bold text-center text-white">
						Admin Login
					</CardTitle>
				</CardHeader>
				<CardContent>
					{loading && <ComponentLoading />}
					{!loading && (
						<Form {...form}>
							<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
								<FormField
									control={control}
									name="username"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium">
												نام کاربری
											</FormLabel>
											<FormControl>
												<Input
													dir="ltr"
													{...field}
													className="bg-gray-700 border-gray-600 focus:ring-blue-500 text-white"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="password"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium">
												رمز عبور
											</FormLabel>
											<FormControl>
												<Input
													dir="ltr"
													type="password"
													{...field}
													className="bg-gray-700 border-gray-600 focus:ring-blue-500 text-white"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<Button
									type="submit"
									disabled={loading}
									className="w-full bg-blue-600 hover:bg-blue-700 font-medium"
								>
									Login
								</Button>
							</form>
						</Form>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

export default LoginPage;
